import { FinanceChamadosWorkflow } from '@/components/Financeiro/FinanceChamadosWorkflow'
import { FinanceQueueFilters } from '@/components/Financeiro/FinanceQueueFilters'
import { FinanceSlaBadge } from '@/components/Financeiro/FinanceSlaBadge'
import { getFinanceRequestClientDisplay, financeQueueRowClass, isFinanceQueueAwaitingFinance } from '@/components/Financeiro/financeQueueHelpers'
import { FinanceRequestClientKindBadge } from '@/components/Financeiro/FinanceRequestClientKindBadge'
import {
  FinanceDataTable,
  financeTableHeadClass,
} from '@/components/Financeiro/FinanceDataTable'
import { cn } from '@/utils/cn'
import { FINANCE_AREA_META, type FinanceAreaKey } from '@/components/Financeiro/solicitacoes/financeSolicitacoesConfig'
import { formatFinanceRequestStatus } from '@/components/Financeiro/financeC4ContractStatus'
import { formatFinanceStatus } from '@/hooks/useFinanceiroPage'
import type { FinanceAssignee, FinanceRequestRow } from '@/services/financeService'
import type { QueueLifecycleFilter, QueueSlaFilter } from '@/hooks/useFinanceiroPage'

type Props = {
  requests: FinanceRequestRow[]
  typeLabels?: Record<string, string>
  onOpenRequest: (id: string) => void
  canManageQueue?: boolean
  queueLifecycle: QueueLifecycleFilter
  setQueueLifecycle: (v: QueueLifecycleFilter) => void
  queueSearch: string
  setQueueSearch: (v: string) => void
  queueStatus: string
  setQueueStatus: (v: string) => void
  queueArea: string
  setQueueArea: (v: string) => void
  queueType: string
  setQueueType: (v: string) => void
  queueSla: QueueSlaFilter
  setQueueSla: (v: QueueSlaFilter) => void
  queueAssignee: string
  setQueueAssignee: (v: string) => void
  queueUnassigned: boolean
  setQueueUnassigned: (v: boolean) => void
  assignees: FinanceAssignee[]
  typeOptions: { key: string; label: string }[]
}

function formatTypeLabel(type: string, typeLabels?: Record<string, string>) {
  return typeLabels?.[type] ?? type.replace(/_/g, ' ')
}

function formatAreaLabel(area: string) {
  return FINANCE_AREA_META[area as FinanceAreaKey]?.label ?? area
}

function formatRequestStatus(status: string, type: string) {
  return type === 'C4_CONTRATO_AGRADA'
    ? formatFinanceRequestStatus(status, type)
    : formatFinanceStatus(status)
}

export function FinanceQueueSection({
  requests,
  typeLabels,
  onOpenRequest,
  canManageQueue = false,
  queueLifecycle,
  setQueueLifecycle,
  queueSearch,
  setQueueSearch,
  queueStatus,
  setQueueStatus,
  queueArea,
  setQueueArea,
  queueType,
  setQueueType,
  queueSla,
  setQueueSla,
  queueAssignee,
  setQueueAssignee,
  queueUnassigned,
  setQueueUnassigned,
  assignees,
  typeOptions,
}: Props) {
  const headClass = financeTableHeadClass()

  return (
    <div className='space-y-5'>
      {canManageQueue && (
        <FinanceQueueFilters
          queueLifecycle={queueLifecycle}
          setQueueLifecycle={setQueueLifecycle}
          queueSearch={queueSearch}
          setQueueSearch={setQueueSearch}
          queueStatus={queueStatus}
          setQueueStatus={setQueueStatus}
          queueArea={queueArea}
          setQueueArea={setQueueArea}
          queueType={queueType}
          setQueueType={setQueueType}
          queueSla={queueSla}
          setQueueSla={setQueueSla}
          queueAssignee={queueAssignee}
          setQueueAssignee={setQueueAssignee}
          queueUnassigned={queueUnassigned}
          setQueueUnassigned={setQueueUnassigned}
          assignees={assignees}
          typeOptions={typeOptions}
        />
      )}

      <FinanceChamadosWorkflow />

      <FinanceDataTable>
        <table className='w-full text-sm'>
          <thead>
            <tr className={headClass}>
              <th className='px-4 py-3'>Protocolo</th>
              <th className='px-4 py-3'>Cliente</th>
              <th className='px-4 py-3'>Área</th>
              <th className='px-4 py-3'>Solicitante</th>
              <th className='px-4 py-3'>Status</th>
              <th className='px-4 py-3'>Responsável</th>
              <th className='px-4 py-3'>SLA</th>
              <th className='px-4 py-3'>Aberto em</th>
            </tr>
          </thead>
          <tbody>
            {requests.length === 0 ? (
              <tr>
                <td colSpan={8} className='px-4 py-10 text-center text-text-muted'>
                  Nenhum chamado encontrado com os filtros atuais.
                </td>
              </tr>
            ) : (
              requests.map((r) => {
                const awaitingFinance = isFinanceQueueAwaitingFinance(r.status)
                const clientDisplay = getFinanceRequestClientDisplay(r)
                return (
                <tr
                  key={r.id}
                  className={financeQueueRowClass(r.status)}
                  onClick={() => onOpenRequest(r.id)}
                >
                  <td
                    className={cn(
                      'px-4 py-3 font-mono text-xs',
                      awaitingFinance && 'font-semibold text-accent',
                    )}
                  >
                    {r.protocol}
                  </td>
                  <td className='px-4 py-3'>
                    <div className='flex items-start gap-2'>
                      <FinanceRequestClientKindBadge kind={clientDisplay.kind} />
                      <div className='min-w-0'>
                        <div
                          className={cn(
                            'font-medium',
                            clientDisplay.kind === 'interno'
                              ? 'text-text-muted italic'
                              : 'text-text-primary',
                          )}
                        >
                          {clientDisplay.label}
                        </div>
                        {clientDisplay.hint && (
                          <div className='mt-0.5 text-[11px] text-text-muted'>{clientDisplay.hint}</div>
                        )}
                        <div className='mt-0.5 text-xs text-text-muted'>
                          {formatTypeLabel(r.type, typeLabels)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className='px-4 py-3 text-text-muted'>{formatAreaLabel(r.area)}</td>
                  <td className='px-4 py-3 text-text-muted'>{r.requester?.name ?? '—'}</td>
                  <td className='px-4 py-3'>
                    <span
                      className={cn(
                        'inline-flex rounded-full px-2 py-0.5 text-xs font-semibold',
                        awaitingFinance
                          ? 'bg-accent/15 text-accent ring-1 ring-accent/20'
                          : 'text-text-primary',
                      )}
                    >
                      {formatRequestStatus(r.status, r.type)}
                    </span>
                  </td>
                  <td className='px-4 py-3 text-text-muted'>{r.assignee?.name ?? '—'}</td>
                  <td className='px-4 py-3'>
                    <FinanceSlaBadge slaDueAt={r.slaDueAt} status={r.status} />
                  </td>
                  <td className='px-4 py-3 text-text-muted'>
                    {new Date(r.createdAt).toLocaleDateString('pt-BR')}
                  </td>
                </tr>
              )})
            )}
          </tbody>
        </table>
      </FinanceDataTable>
    </div>
  )
}
